import { useState, type Dispatch, type SetStateAction } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, ChevronUp, User, Tag } from "lucide-react";
import { Label } from "../../../app/components/ui/label";
import { Input } from "../../../app/components/ui/input";
import { Button } from "../../../app/components/ui/button";
import { PosBillSummary } from "../../pos/components/PosBillSummary";
import { PosCartItemCard } from "../../pos/components/PosCartItemCard";
import type { PosCartItem, PosGstLineBreakdown } from "../../pos/types";
import { formatInrTrimmed } from "../../../lib/currency";
import { PaymentSectionV2, type PosPaymentMethod } from "./PaymentSectionV2";
import type { PosQuote } from "../../pos/hooks/usePosSession";

type CartLine = {
  item: PosCartItem;
  gst: PosGstLineBreakdown;
};

type CartPanelV2Props = {
  cart: PosCartItem[];
  cartLines: CartLine[];
  expandedCartPid: string | null;
  setExpandedCartPid: Dispatch<SetStateAction<string | null>>;
  setCart: Dispatch<SetStateAction<PosCartItem[]>>;
  customerPhone: string;
  setCustomerPhone: (v: string) => void;
  customerName: string;
  setCustomerName: (v: string) => void;
  promoCode: string;
  setPromoCode: (v: string) => void;
  paymentMethod: PosPaymentMethod;
  onPaymentMethodChange: (m: PosPaymentMethod) => void;
  quote: PosQuote | null;
  totals: { taxable: number; gst: number; grand: number };
  showComplianceWarning: boolean;
  setItemQty: (productId: string, nextQty: number) => void;
  onCheckout: () => void;
  checkoutLoading: boolean;
};

export function CartPanelV2({
  cart,
  cartLines,
  expandedCartPid,
  setExpandedCartPid,
  setCart,
  customerPhone,
  setCustomerPhone,
  customerName,
  setCustomerName,
  promoCode,
  setPromoCode,
  paymentMethod,
  onPaymentMethodChange,
  quote,
  totals,
  showComplianceWarning,
  setItemQty,
  onCheckout,
  checkoutLoading,
}: CartPanelV2Props) {
  const [customerExpanded, setCustomerExpanded] = useState(true);
  const grand = quote ? Number.parseFloat(quote.totals.grand_total) : totals.grand;

  return (
    <div className="flex h-full min-h-0 w-full max-w-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
      <div className="shrink-0 border-b border-border p-5">
        <h2 className="font-semibold text-foreground">Current Sale</h2>
        <p className="text-sm text-muted-foreground">{cart.length} items</p>
      </div>

      <div className="shrink-0 border-b border-border p-3">
        <button
          type="button"
          onClick={() => setCustomerExpanded((v) => !v)}
          className="flex w-full items-center justify-between rounded-xl bg-muted p-3 text-left transition-colors hover:bg-accent"
        >
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-muted-foreground" />
            <span className="font-medium text-foreground">Customer Info</span>
          </div>
          {customerExpanded ? (
            <ChevronUp className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          )}
        </button>

        <AnimatePresence>
          {customerExpanded ? (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-3 space-y-3 px-0.5">
                <div className="space-y-1.5">
                  <Label htmlFor="posv2-customer-phone" className="text-foreground">
                    Mobile
                  </Label>
                  <Input
                    id="posv2-customer-phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="10-digit mobile"
                    className="rounded-xl border-border bg-input-background"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="posv2-customer-name" className="text-foreground">
                    Customer name
                  </Label>
                  <Input
                    id="posv2-customer-name"
                    type="text"
                    placeholder="Name"
                    className="rounded-xl border-border bg-input-background"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground">Payment</Label>
                  <PaymentSectionV2 value={paymentMethod} onChange={onPaymentMethodChange} />
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      <div className="min-h-0 min-w-0 flex-1 space-y-2 overflow-y-auto p-3 sm:p-4">
        {cart.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
            Add products to start billing
          </div>
        ) : (
          cartLines.map(({ item, gst }) => (
            <PosCartItemCard
              key={item.product_id}
              item={item}
              gst={gst}
              expanded={expandedCartPid === item.product_id}
              onExpandedChange={(open) => {
                setExpandedCartPid((cur) => {
                  if (open) return item.product_id;
                  return cur === item.product_id ? null : cur;
                });
              }}
              onQtyDelta={(q) => setItemQty(item.product_id, q)}
              onChangeOverride={(patch) => {
                setCart((prev) => prev.map((ci) => (ci.product_id === item.product_id ? { ...ci, ...patch } : ci)));
              }}
            />
          ))
        )}
      </div>

      <div className="shrink-0 space-y-4 border-t border-border bg-muted/40 p-5">
        <PosBillSummary
          subtotalTaxable={quote ? Number.parseFloat(quote.totals.taxable) : totals.taxable}
          discountTotal={quote ? Number.parseFloat(quote.totals.discount) : 0}
          gstTotal={quote ? Number.parseFloat(quote.totals.gst) : totals.gst}
          grandTotal={grand}
          showComplianceWarning={showComplianceWarning}
          appliedPromotionLabel={
            quote?.applied_promotion
              ? `${quote.applied_promotion.name}${quote.applied_promotion.code ? ` (${quote.applied_promotion.code})` : ""}`
              : null
          }
        />

        <div className="flex gap-2">
          <Input
            type="text"
            placeholder="Promo code"
            className="flex-1 rounded-xl border-border bg-input-background"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
          />
          <Button type="button" variant="outline" size="icon" className="shrink-0 rounded-xl border-border" aria-label="Promo">
            <Tag className="h-5 w-5" />
          </Button>
        </div>

        <Button
          type="button"
          className="h-12 w-full rounded-xl bg-primary text-base font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          onClick={() => void onCheckout()}
          disabled={checkoutLoading || cart.length === 0}
        >
          {checkoutLoading ? "Completing…" : `Complete Sale ${formatInrTrimmed(grand)}`}
        </Button>
      </div>
    </div>
  );
}
